import { DIRECTORY_CAPABILITIES } from '@tabel/shared/personnel';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Button, Callout, Field, inputCls, textareaCls } from '../../components/ui';
import {
  type Assignment,
  type Brigade,
  type Envelope,
  type Preview,
  dateTime,
  directoryRequest,
} from '../../lib/personnel-api';
import { BrigadePicker, useBrigadeOptions } from './BrigadePicker';
import { CAPABILITY_LABEL, DateFields, ErrorNotice, PanelSection, PreviewCommit, StateBadge } from './parts';

type AccountOption = {
  userId: number | string;
  kind?: 'human' | 'unknown';
  boundPersonId?: string | null;
  displayName: string;
  login?: string;
  state?: string;
  accountStatus?: string;
  personId?: string | null;
};
type BindingView = {
  items: {
    id: string;
    userId: number | string;
    personId: string;
    state: string;
    revision: number;
    login?: string;
    accountStatus?: string;
    fromMs?: number | null;
    toMs?: number | null;
  }[];
};
const ROLE_LABEL: Record<string, string> = {
  company_operator: 'Оператор компании',
  brigade_primary: 'Основной бригадир',
  brigade_representative: 'Представитель бригады',
  site_master: 'Мастер площадки',
};
type AccessCommand = {
  action: string;
  userId: number;
  personId?: string;
  bindingId?: string;
  changeKind?: 'link_correction' | 'account_replacement';
  accountKind?: string;
  assignmentId?: string;
  role?: string;
  scope?: { kind: string; brigadeId?: string; siteId?: string };
  range?: { fromMs: number | null; toMs: number | null };
  allowedActions?: string[];
  basis: string;
};
const utcInstant = (value: number | null) =>
  value == null ? 'начало не указано' : new Date(value).toISOString();
export function AccountSection({
  personId,
  personDisplayName,
  caps,
  onChanged,
}: { personId: string; personDisplayName?: string | null; caps: string[]; onChanged: () => void }) {
  const binding = useQuery({
    queryKey: ['personnel', 'binding', personId],
    queryFn: () =>
      directoryRequest<BindingView>(`/api/personnel/people/${encodeURIComponent(personId)}/account-binding`),
    enabled: caps.includes('accounts.person_bind'),
  });
  const assignments = useQuery({
    queryKey: ['personnel', 'assignments', personId],
    queryFn: () =>
      directoryRequest<{ items: Assignment[] }>(
        `/api/personnel/assignments?personId=${encodeURIComponent(personId)}`,
      ),
    enabled: caps.includes('assignments.manage'),
  });
  const options = useQuery({
    queryKey: ['personnel', 'account-options'],
    queryFn: () => directoryRequest<{ items: AccountOption[] }>('/api/personnel/account-options'),
    enabled: caps.includes('accounts.person_bind') || caps.includes('assignments.manage'),
  });
  const brigades = useBrigadeOptions('assignment', caps.includes('assignments.manage'));
  const scopeOptions = useQuery({
    queryKey: ['personnel', 'capabilities'],
    queryFn: () =>
      directoryRequest<{
        sites?: { id: string; name: string }[];
        scopes: { capability: string; scope: { kind: string; siteId?: string } }[];
      }>('/api/personnel/capabilities'),
    enabled: caps.includes('assignments.manage'),
  });
  const [userId, setUserId] = useState('');
  const [basis, setBasis] = useState('');
  const [action, setAction] = useState<'bind' | 'replace' | 'end'>('bind');
  const [changeKind, setChangeKind] = useState<'link_correction' | 'account_replacement'>(
    'account_replacement',
  );
  const [role, setRole] = useState('brigade_representative');
  const [brigadeId, setBrigadeId] = useState('');
  const [selectedBrigade, setSelectedBrigade] = useState<Brigade | null>(null);
  const [siteId, setSiteId] = useState('');
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 16));
  const [to, setTo] = useState('');
  const [humanConfirmed, setHumanConfirmed] = useState(false);
  const [operatorActions, setOperatorActions] = useState<string[]>([]);
  const [allowedRead, setAllowedRead] = useState(true);
  const [allowedMembership, setAllowedMembership] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [preview, setPreview] = useState<Envelope<Preview> | null>(null);
  const [pendingInput, setPendingInput] = useState<unknown>(null);
  const [commitSummary, setCommitSummary] = useState<ReactNode>(null);
  const [endpoint, setEndpoint] = useState('');
  const current = binding.data?.data.items.find((b) => b.state === 'active') ?? binding.data?.data.items[0];
  const previewCommand = async (path: string, input: AccessCommand) => {
    setError(null);
    const account = options.data?.data.items.find((a) => Number(a.userId) === input.userId);
    const accountBinding = binding.data?.data.items.find((b) => Number(b.userId) === input.userId);
    const previousBinding = binding.data?.data.items.find((b) => b.id === input.bindingId);
    const scopeBrigade =
      selectedBrigade?.id === input.scope?.brigadeId
        ? selectedBrigade
        : brigades.items.find((b) => b.id === input.scope?.brigadeId);
    const scopeSite = scopeOptions.data?.data.sites?.find((s) => s.id === input.scope?.siteId);
    const isBinding = path === '/api/personnel/account-bindings';
    const futureCancellation =
      !isBinding && input.action === 'end' && input.range?.fromMs != null && input.range.fromMs > Date.now();
    const actionLabel = isBinding
      ? ({
          bind: 'Привязать существующий аккаунт',
          replace: 'Заменить связь с аккаунтом',
          end: 'Завершить привязку аккаунта',
        }[input.action] ?? 'Изменить привязку аккаунта')
      : futureCancellation
        ? 'Отменить будущее назначение'
        : ({
            grant: 'Выдать назначение',
            end: 'Завершить назначение',
            transfer_primary: 'Передать полномочия основного бригадира',
          }[input.action] ?? 'Изменить назначение');
    const summary = (
      <div className="space-y-2">
        <p className="font-medium">{actionLabel}</p>
        <p>
          Открытая карточка: {personDisplayName ?? 'Имя не получено'} · Company ID {personId}
        </p>
        <p>
          Аккаунт: {account?.displayName ?? 'Название аккаунта не предоставлено'} · User ID {input.userId}
        </p>
        <p>Логин: {account?.login ?? accountBinding?.login ?? 'не предоставлен в этой проекции'}</p>
        {account?.boundPersonId && account.boundPersonId !== personId && (
          <p>Связанная карточка выбранного аккаунта: Company ID {account.boundPersonId}</p>
        )}
        {input.bindingId && <p>Изменяемая привязка: {input.bindingId}</p>}
        {input.action === 'replace' && (
          <>
            <p>
              Причина замены:{' '}
              {input.changeKind === 'link_correction'
                ? 'исправление ошибочной кадровой связи владельца'
                : 'другой аккаунт того же человека'}
            </p>
            {previousBinding && (
              <p>
                Прежний аккаунт: {previousBinding.login ?? 'Логин не предоставлен'} · User ID{' '}
                {previousBinding.userId}
              </p>
            )}
          </>
        )}
        {isBinding ? (
          <>
            <p>
              {input.action === 'end'
                ? 'Текущая привязка завершится в момент сохранения.'
                : input.action === 'replace'
                  ? 'Прежняя привязка завершится и новая начнётся в момент сохранения, без ограничения конца.'
                  : 'Новая привязка начнётся в момент сохранения, без ограничения конца.'}
            </p>
            {input.accountKind === 'human' && <p>Подтверждён личный аккаунт человека.</p>}
          </>
        ) : (
          <>
            <p>Роль: {ROLE_LABEL[input.role ?? ''] ?? 'Роль не предоставлена'}</p>
            <p>
              Область:{' '}
              {input.scope?.kind === 'company'
                ? 'Компания'
                : input.scope?.kind === 'brigade'
                  ? `${scopeBrigade?.name ?? 'Название бригады не предоставлено'} · Brigade ID ${input.scope.brigadeId}`
                  : `${scopeSite?.name ?? 'Название площадки не предоставлено'} · Site ID ${input.scope?.siteId}`}
            </p>
            {input.assignmentId && <p>Изменяемое назначение: {input.assignmentId}</p>}
            {input.range && (
              <p>
                Период назначения (UTC): {utcInstant(input.range.fromMs)} —{' '}
                {input.range.toMs == null
                  ? 'без ограничения конца'
                  : `${utcInstant(input.range.toMs)} (окончание не включая)`}
              </p>
            )}
            {futureCancellation && (
              <p>Будущее назначение отменяется. Исходный период сохраняется в истории.</p>
            )}
            <p>
              Полномочия:{' '}
              {input.allowedActions?.map((a) => CAPABILITY_LABEL[a] ?? a).join(', ') || 'не выбраны'}
            </p>
          </>
        )}
        <p className="whitespace-pre-wrap break-words">Основание: {input.basis}</p>
      </div>
    );
    try {
      const r = await directoryRequest<Preview>(`${path}/preview`, 'POST', input);
      setCommitSummary(summary);
      setPreview(r);
      setPendingInput(input);
      setEndpoint(path);
    } catch (e) {
      setError(e);
    }
  };
  const humanAccounts = options.data?.data.items ?? [];
  const fixedPrimary = [
    'brigade.read',
    'brigade.rename',
    'brigade.manage',
    'membership.read',
    'membership.manage',
    'personnel.select',
  ];
  const fixedMaster = ['personnel.read', 'personnel.select', 'membership.read'];
  const accountField = (onlyBound = false) => (
    <Field label="Учётная запись человека">
      <select
        aria-label="Учётная запись человека"
        className={inputCls}
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
      >
        <option value="">Выберите существующий аккаунт</option>
        {humanAccounts
          .filter(
            (u) => !onlyBound || (u.kind === 'human' && Boolean(u.boundPersonId) && u.state === 'active'),
          )
          .map((u) => (
            <option key={u.userId} value={u.userId}>
              {u.displayName}
              {u.login ? ` · ${u.login}` : ''}
              {u.accountStatus === 'blocked' || u.state === 'blocked' ? ' · заблокирован' : ''}
            </option>
          ))}
      </select>
    </Field>
  );
  return (
    <div className="space-y-5">
      <Callout icon={Info}>
        Обычному работнику аккаунт не требуется. Привязка человека, назначение роли и блокировка аккаунта —
        отдельные действия. Права прежних модулей сохраняются.
      </Callout>
      <ErrorNotice error={binding.error ?? assignments.error ?? options.error} />
      {caps.includes('accounts.person_bind') && (
        <PanelSection title="Связь с аккаунтом">
          {binding.data?.data.items.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center gap-2 text-sm">
              <span>{b.login ?? `Аккаунт ${b.userId}`}</span>
              <StateBadge state={b.state} />
              <span className="text-xs text-ink-3">
                {dateTime(b.fromMs)} — {b.toMs == null ? 'без окончания' : dateTime(b.toMs)}
              </span>
            </div>
          ))}
          {!binding.data?.data.items.length && <p className="text-sm text-ink-2">Аккаунт не привязан.</p>}
          <Field label="Действие с привязкой">
            <select
              aria-label="Действие с привязкой"
              className={inputCls}
              value={action}
              onChange={(e) => setAction(e.target.value as typeof action)}
            >
              <option value="bind">Привязать существующий аккаунт</option>
              {current && (
                <>
                  <option value="replace">Заменить ошибочную связь / аккаунт</option>
                  <option value="end">Завершить привязку</option>
                </>
              )}
            </select>
          </Field>
          {accountField()}
          {action === 'replace' && (
            <Field label="Причина замены">
              <select
                aria-label="Причина замены"
                className={inputCls}
                value={changeKind}
                onChange={(e) => setChangeKind(e.target.value as typeof changeKind)}
              >
                <option value="account_replacement">Другой аккаунт того же человека</option>
                <option value="link_correction">Исправление ошибочной кадровой связи владельца</option>
              </select>
            </Field>
          )}
          <label className="flex min-h-10 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={humanConfirmed}
              onChange={(e) => setHumanConfirmed(e.target.checked)}
            />
            Подтверждаю, что это личный аккаунт человека
          </label>
          <Field label="Основание привязки">
            <textarea
              aria-label="Основание привязки"
              className={textareaCls}
              value={basis}
              onChange={(e) => setBasis(e.target.value)}
            />
          </Field>
          <Button
            disabled={!basis.trim() || (!userId && action !== 'end') || (action !== 'end' && !humanConfirmed)}
            onClick={() =>
              void previewCommand('/api/personnel/account-bindings', {
                action,
                ...(current && action !== 'bind' ? { bindingId: current.id } : {}),
                ...(action === 'replace' ? { changeKind } : {}),
                userId: Number(userId || current?.userId),
                personId,
                ...(action === 'end' ? {} : { accountKind: 'human' }),
                basis: basis.trim(),
              })
            }
          >
            Проверить привязку
          </Button>
        </PanelSection>
      )}
      {caps.includes('assignments.manage') && (
        <PanelSection title="Назначения в справочнике">
          {assignments.data?.data.items.map((a) => (
            <div key={a.id} className="space-y-2 rounded-control border border-line p-3">
              <p className="text-sm font-medium">{ROLE_LABEL[a.role] ?? a.role}</p>
              <p className="text-xs text-ink-2">
                {a.scope.kind === 'company' ? 'Компания' : (a.scope.brigadeId ?? a.scope.siteId)} ·{' '}
                {dateTime(a.fromMs)} — {a.toMs == null ? 'без окончания' : dateTime(a.toMs)}
              </p>
              <Button
                size="sm"
                onClick={() =>
                  void previewCommand('/api/personnel/assignments', {
                    action: 'end',
                    assignmentId: a.id,
                    role: a.role,
                    userId: Number(a.userId),
                    scope: a.scope,
                    range: {
                      fromMs: a.fromMs ?? null,
                      toMs: a.fromMs != null && a.fromMs > Date.now() ? (a.toMs ?? null) : Date.now(),
                    },
                    allowedActions: a.allowedActions ?? [],
                    basis: basis.trim(),
                  })
                }
                disabled={!basis.trim()}
              >
                {a.fromMs != null && a.fromMs > Date.now()
                  ? 'Отменить будущее назначение'
                  : 'Завершить назначение'}
              </Button>
            </div>
          ))}
          {accountField(true)}
          <Field label="Роль в справочнике">
            <select
              aria-label="Роль в справочнике"
              className={inputCls}
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="brigade_representative">Представитель бригады — только чтение</option>
              <option value="brigade_primary">Основной бригадир</option>
              <option value="site_master">Мастер площадки</option>
              <option value="company_operator">Оператор справочника компании</option>
            </select>
          </Field>
          {role.startsWith('brigade_') && (
            <BrigadePicker
              label="Бригада"
              searchLabel="Найти бригаду для назначения"
              options={brigades}
              value={brigadeId}
              selected={selectedBrigade}
              onSelect={(brigade) => {
                setSelectedBrigade(brigade);
                setBrigadeId(brigade?.id ?? '');
              }}
            />
          )}
          {role === 'site_master' && (
            <Field label="Площадка">
              <select
                aria-label="Площадка"
                className={inputCls}
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
              >
                <option value="">Выберите разрешённую площадку</option>
                {[
                  ...new Set([
                    ...(scopeOptions.data?.data.sites?.map((s) => s.id) ?? []),
                    ...(scopeOptions.data?.data.scopes.flatMap((s) =>
                      s.scope.kind === 'site' && s.scope.siteId ? [s.scope.siteId] : [],
                    ) ?? []),
                  ]),
                ].map((s) => (
                  <option key={s} value={s}>
                    {scopeOptions.data?.data.sites?.find((site) => site.id === s)?.name ?? s}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {role === 'brigade_representative' && (
            <div className="space-y-2 text-sm">
              <label className="flex min-h-10 items-center gap-2">
                <input
                  type="checkbox"
                  checked={allowedRead}
                  onChange={(e) => setAllowedRead(e.target.checked)}
                />
                Чтение бригады
              </label>
              <label className="flex min-h-10 items-center gap-2">
                <input
                  type="checkbox"
                  checked={allowedMembership}
                  onChange={(e) => setAllowedMembership(e.target.checked)}
                />
                Чтение состава
              </label>
            </div>
          )}
          {role === 'company_operator' && (
            <fieldset className="space-y-1">
              <legend className="mb-2 text-sm font-medium">Явно выдаваемые полномочия</legend>
              {DIRECTORY_CAPABILITIES.filter((c) => caps.includes(c)).map((c) => (
                <label key={c} className="flex min-h-10 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={operatorActions.includes(c)}
                    onChange={(e) =>
                      setOperatorActions(
                        e.target.checked ? [...operatorActions, c] : operatorActions.filter((v) => v !== c),
                      )
                    }
                  />
                  {CAPABILITY_LABEL[c] ?? c}
                </label>
              ))}
            </fieldset>
          )}
          <DateFields utc from={from} to={to} setFrom={setFrom} setTo={setTo} />
          <Field label="Основание назначения">
            <textarea
              aria-label="Основание назначения"
              className={textareaCls}
              value={basis}
              onChange={(e) => setBasis(e.target.value)}
            />
          </Field>
          <Button
            disabled={
              !userId ||
              !basis.trim() ||
              !from ||
              (role.startsWith('brigade_') && !brigadeId) ||
              (role === 'site_master' && !siteId)
            }
            onClick={() =>
              void previewCommand('/api/personnel/assignments', {
                action:
                  role === 'brigade_primary' && selectedBrigade?.primaryLeader ? 'transfer_primary' : 'grant',
                ...(role === 'brigade_primary' && selectedBrigade?.primaryLeader
                  ? { assignmentId: selectedBrigade.primaryLeader.assignmentId }
                  : {}),
                role,
                userId: Number(userId),
                scope: role.startsWith('brigade_')
                  ? { kind: 'brigade', brigadeId }
                  : role === 'site_master'
                    ? { kind: 'site', siteId }
                    : { kind: 'company' },
                range: { fromMs: Date.parse(`${from}:00Z`), toMs: to ? Date.parse(`${to}:00Z`) : null },
                allowedActions:
                  role === 'brigade_representative'
                    ? [
                        ...(allowedRead ? ['brigade.read'] : []),
                        ...(allowedMembership ? ['membership.read'] : []),
                      ]
                    : role === 'brigade_primary'
                      ? fixedPrimary
                      : role === 'site_master'
                        ? fixedMaster
                        : operatorActions,
                basis: basis.trim(),
              })
            }
          >
            Проверить назначение
          </Button>
        </PanelSection>
      )}
      <ErrorNotice error={error} />
      {preview && (
        <PreviewCommit
          title="Подтвердить изменение аккаунта и доступа?"
          summary={commitSummary}
          preview={preview}
          input={pendingInput}
          endpoint={endpoint}
          onCancel={() => setPreview(null)}
          onDone={() => {
            setPreview(null);
            void binding.refetch();
            void assignments.refetch();
            onChanged();
          }}
        />
      )}
    </div>
  );
}
